import { useState, useEffect } from "react";
import { Trophy, Target, Plus, Users, Flame, Droplets, Footprints, Apple, Moon, Award, X, ClipboardCheck, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Navbar } from "../components/Navbar";
import { ProtectedRoute } from "../components/ProtectedRoute";
import api from "../lib/api";

const CHALLENGE_ICONS = {
  workout: Flame,
  water: Droplets,
  steps: Footprints,
  nutrition: Apple,
  sleep: Moon,
};

const CHALLENGE_COLORS = {
  workout: { bg: "bg-orange-50", text: "text-orange-600", accent: "bg-orange-500" },
  water: { bg: "bg-blue-50", text: "text-blue-600", accent: "bg-blue-500" },
  steps: { bg: "bg-green-50", text: "text-green-600", accent: "bg-green-500" },
  nutrition: { bg: "bg-purple-50", text: "text-purple-600", accent: "bg-purple-500" },
  sleep: { bg: "bg-indigo-50", text: "text-indigo-600", accent: "bg-indigo-500" },
};

const LOG_FIELDS = {
  workout: { key: "workout_minutes", label: "Workout minutes", type: "number", placeholder: "e.g. 45" },
  water: { key: "water_liters", label: "Water (liters)", type: "number", placeholder: "e.g. 3.0", step: "0.1" },
  steps: { key: "steps", label: "Steps", type: "number", placeholder: "e.g. 12000" },
  nutrition: { key: "calories_consumed", label: "Calories consumed", type: "number", placeholder: "e.g. 2000" },
  sleep: { key: "sleep_hours", label: "Sleep hours", type: "number", placeholder: "e.g. 8", step: "0.5" },
};

const ChallengesContent = () => {
  const [challenges, setChallenges] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [logModal, setLogModal] = useState(null);
  const [logValue, setLogValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [newChallenge, setNewChallenge] = useState({ title: "", description: "", challenge_type: "workout", target_value: 30, duration_days: 30, reward_badge: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chRes, badgeRes] = await Promise.all([
        api.get("/challenges").catch(() => ({ data: [] })),
        api.get("/badges").catch(() => ({ data: [] })),
      ]);
      setChallenges(chRes.data || []);
      setBadges(badgeRes.data || []);
    } catch (err) { console.error('Error:', err.message || err); }
    setLoading(false);
  };

  const joinChallenge = async (id) => {
    try {
      await api.post(`/challenges/${id}/join`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const submitLog = async () => {
    if (!logModal || !logValue) return;
    setSubmitting(true);
    const field = LOG_FIELDS[logModal.challenge_type];
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.post("/goals/log", { date: today, [field.key]: parseFloat(logValue) });
      await api.post(`/challenges/${logModal.challenge_id}/verify`);
      setLogModal(null);
      setLogValue("");
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed to log"); }
    setSubmitting(false);
  };

  const createChallenge = async () => {
    if (!newChallenge.title) return;
    try {
      await api.post("/challenges", { ...newChallenge, target_value: parseInt(newChallenge.target_value), duration_days: parseInt(newChallenge.duration_days) });
      setShowCreate(false);
      setNewChallenge({ title: "", description: "", challenge_type: "workout", target_value: 30, duration_days: 30, reward_badge: "" });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Failed"); }
  };

  const seedChallenges = async () => {
    try { await api.post("/admin/seed-challenges"); fetchData(); } catch (err) { console.error('Error:', err.message || err); }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="challenges-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-amber-500 p-2.5 rounded-xl"><Trophy className="h-6 w-6 text-white" /></div>
              <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold text-[#0F172A]">Challenges</h1>
            </div>
            <p className="text-slate-500 ml-14">Log real health data to earn progress</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-challenge-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6 font-semibold shadow-lg">
            <Plus className="mr-2 h-4 w-4" /> New Challenge
          </Button>
        </div>

        <Tabs defaultValue="challenges">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm border border-slate-100 mb-6">
            <TabsTrigger value="challenges" data-testid="tab-challenges" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium">
              <Target className="mr-2 h-4 w-4" /> Active Challenges
            </TabsTrigger>
            <TabsTrigger value="badges" data-testid="tab-badges" className="rounded-lg data-[state=active]:bg-[#F2F9F6] data-[state=active]:text-[#0B6E4F] font-medium">
              <Award className="mr-2 h-4 w-4" /> My Badges ({badges.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="challenges">
            {loading ? (
              <div className="text-center py-16"><div className="h-10 w-10 border-3 border-[#0B6E4F] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : challenges.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-16 shadow-sm text-center" data-testid="no-challenges">
                <Trophy className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No active challenges yet</p>
                <Button onClick={seedChallenges} data-testid="seed-challenges-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6">
                  Load Default Challenges
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {challenges.map((ch) => {
                  const colors = CHALLENGE_COLORS[ch.challenge_type] || CHALLENGE_COLORS.workout;
                  const Icon = CHALLENGE_ICONS[ch.challenge_type] || Target;
                  const pct = ch.target_value > 0 ? Math.min((ch.user_progress / ch.target_value) * 100, 100) : 0;
                  const done = pct >= 100;
                  return (
                    <div key={ch.challenge_id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all" data-testid={`challenge-${ch.challenge_id}`}>
                      <div className="flex items-start gap-4 mb-3">
                        <div className={`${colors.bg} p-3 rounded-xl`}><Icon className={`h-6 w-6 ${colors.text}`} /></div>
                        <div className="flex-1">
                          <h3 className="font-['Outfit'] text-lg font-semibold text-[#0F172A]">{ch.title}</h3>
                          <p className="text-sm text-slate-500 mt-1 leading-relaxed">{ch.description}</p>
                        </div>
                      </div>

                      {/* Verification rule */}
                      <div className={`${colors.bg} rounded-lg px-3 py-2 mb-3 flex items-center gap-2`}>
                        <ClipboardCheck className={`h-4 w-4 ${colors.text}`} />
                        <span className={`text-xs font-medium ${colors.text}`}>How to progress: {ch.verification_rule}</span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-slate-400 mb-4">
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ch.participants_count} joined</span>
                        <span>{ch.duration_days} days</span>
                        {ch.user_joined && ch.user_streak > 0 && (
                          <span className="flex items-center gap-1 text-amber-500 font-semibold"><Zap className="h-3.5 w-3.5" /> {ch.user_streak} day streak</span>
                        )}
                      </div>

                      {ch.user_joined && (
                        <div className="mb-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="font-medium text-slate-700">Verified Days</span>
                            <span className={`font-bold ${colors.text}`}>{ch.user_progress}/{ch.target_value}</span>
                          </div>
                          <Progress value={pct} className="h-2.5" />
                        </div>
                      )}

                      {ch.leaderboard?.length > 0 && (
                        <div className="mb-4 pt-3 border-t border-slate-50">
                          <p className="text-xs font-medium text-slate-400 mb-2 uppercase tracking-wide">Leaderboard</p>
                          <div className="space-y-1.5">
                            {ch.leaderboard.slice(0, 3).map((entry, i) => (
                              <div key={`${entry.user_name}-${i}`} className="flex items-center gap-2 text-xs">
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-400" : "bg-amber-700"}`}>{i + 1}</span>
                                <span className="flex-1 text-slate-600 truncate">{entry.user_name}</span>
                                <span className="font-semibold text-slate-800">{entry.current_value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2">
                        {!ch.user_joined ? (
                          <Button onClick={() => joinChallenge(ch.challenge_id)} data-testid={`join-challenge-${ch.challenge_id}`} className="flex-1 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full text-sm font-semibold">
                            Join Challenge
                          </Button>
                        ) : done ? (
                          <div className="flex-1 bg-[#D4F4DD] text-[#0B6E4F] rounded-full py-2 text-center text-sm font-bold" data-testid={`completed-${ch.challenge_id}`}>
                            Completed!
                          </div>
                        ) : (
                          <Button onClick={() => { setLogModal(ch); setLogValue(""); }} data-testid={`log-challenge-${ch.challenge_id}`} className={`flex-1 ${colors.accent} hover:opacity-90 text-white rounded-full text-sm font-semibold`}>
                            <ClipboardCheck className="mr-2 h-4 w-4" /> Log Today's Data
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="badges">
            {badges.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-16 shadow-sm text-center" data-testid="no-badges">
                <Award className="h-12 w-12 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500">No badges earned yet</p>
                <p className="text-sm text-slate-400 mt-1">Complete challenges to earn badges!</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {badges.map((b, i) => (
                  <div key={b.badge_name || i} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm text-center" data-testid={`badge-${i}`}>
                    <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="h-8 w-8 text-amber-500" />
                    </div>
                    <h4 className="font-['Outfit'] font-semibold text-[#0F172A]">{b.badge_name}</h4>
                    <p className="text-xs text-slate-400 mt-1">{b.challenge_title}</p>
                    <p className="text-xs text-slate-300 mt-2">Earned {new Date(b.earned_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Log Health Data Modal */}
        {logModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="log-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Outfit'] text-lg font-semibold">Log Today's Data</h3>
                <button onClick={() => setLogModal(null)} className="p-1 hover:bg-slate-100 rounded-full"><X className="h-5 w-5" /></button>
              </div>
              <p className="text-sm text-slate-500 mb-1">{logModal.title}</p>
              <div className={`${CHALLENGE_COLORS[logModal.challenge_type]?.bg || "bg-slate-50"} rounded-lg px-3 py-2 mb-4`}>
                <p className={`text-xs font-medium ${CHALLENGE_COLORS[logModal.challenge_type]?.text || "text-slate-600"}`}>{logModal.verification_rule}</p>
              </div>
              <div className="mb-5">
                <Label className="text-sm font-medium">{LOG_FIELDS[logModal.challenge_type]?.label}</Label>
                <Input
                  type="number"
                  step={LOG_FIELDS[logModal.challenge_type]?.step || "1"}
                  value={logValue}
                  onChange={(e) => setLogValue(e.target.value)}
                  placeholder={LOG_FIELDS[logModal.challenge_type]?.placeholder}
                  className="mt-1 h-11 rounded-xl bg-slate-50"
                  data-testid="log-value-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <Button variant="ghost" onClick={() => setLogModal(null)} className="flex-1">Cancel</Button>
                <Button onClick={submitLog} disabled={!logValue || submitting} data-testid="submit-log-btn" className="flex-1 bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full font-semibold">
                  {submitting ? "Logging..." : "Log & Verify"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Create Challenge Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-challenge-modal">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-['Outfit'] text-lg font-semibold">Create Challenge</h3>
                <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded-full"><X className="h-5 w-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Title</Label>
                  <Input value={newChallenge.title} onChange={(e) => setNewChallenge({ ...newChallenge, title: e.target.value })} placeholder="30-Day Fitness Streak" className="mt-1 h-11 rounded-xl bg-slate-50" data-testid="challenge-title-input" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Description</Label>
                  <textarea value={newChallenge.description} onChange={(e) => setNewChallenge({ ...newChallenge, description: e.target.value })} placeholder="Describe the challenge..." className="w-full h-20 mt-1 bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-[#0B6E4F] outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Type</Label>
                    <select value={newChallenge.challenge_type} onChange={(e) => setNewChallenge({ ...newChallenge, challenge_type: e.target.value })} className="w-full mt-1 h-11 rounded-xl bg-slate-50 border border-slate-200 px-3 text-sm" data-testid="challenge-type-select">
                      <option value="workout">Workout</option>
                      <option value="water">Hydration</option>
                      <option value="steps">Steps</option>
                      <option value="nutrition">Nutrition</option>
                      <option value="sleep">Sleep</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Target (days)</Label>
                    <Input type="number" value={newChallenge.target_value} onChange={(e) => setNewChallenge({ ...newChallenge, target_value: e.target.value })} className="mt-1 h-11 rounded-xl bg-slate-50" data-testid="challenge-target-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Badge Name</Label>
                  <Input value={newChallenge.reward_badge} onChange={(e) => setNewChallenge({ ...newChallenge, reward_badge: e.target.value })} placeholder="Fitness Warrior" className="mt-1 h-11 rounded-xl bg-slate-50" data-testid="challenge-badge-input" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={createChallenge} data-testid="submit-challenge-btn" className="bg-[#0B6E4F] hover:bg-[#095A40] text-white rounded-full px-6">Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Challenges() {
  return <ProtectedRoute><ChallengesContent /></ProtectedRoute>;
}
